/**
 * Spec 084 (gap 084-1) — single-rule detail view.
 *
 * Shows definition, evaluation steps, DAG structure, applicability,
 * provenance, and the version history (from /rules/by-slug/:slug/history).
 * Activate / Deprecate actions are available in the header, gated by the
 * rule's current status — same logic as `RuleLibraryPage`.
 *
 * `:id` is always a UUID. React Router resolves literal segments
 * (`/admin/rules/candidates`, `/admin/rules/suggestions`) before param
 * segments at the same depth, so those paths never land here even if the
 * route order were inverted.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  activateRule,
  deprecateRule,
  getRule,
  getRuleHistory,
  validateRuleProvenance,
  type ProvenanceValidation,
  type Rule,
} from '../../api/rules.js';
import { useToast } from '../../hooks/useToast.js';
import { DeprecateModal } from './DeprecateModal.js';
import { EpistemicBadge, StatusBadge } from './RuleLibraryPage.js';
import { Breadcrumbs, type BreadcrumbItem } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { SeedBadge } from '../../components/SeedBadge.js';
import { Chip as KridtChip } from '../../components/ui/Chip.js';
import { Icon } from '../../components/ui/Icon.js';
import { Button } from '../../components/ui/Button.js';

const BASE_CRUMBS: BreadcrumbItem[] = [
  { label: 'Observatory', to: '/admin' },
  { label: 'Rule library', to: '/admin/rules' },
];

export function RuleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [rule, setRule] = useState<Rule | null>(null);
  const [history, setHistory] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [showDeprecate, setShowDeprecate] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [provValidation, setProvValidation] = useState<Map<string, ProvenanceValidation> | null>(null);
  const [validating, setValidating] = useState(false);

  const onValidateProvenance = useCallback(async () => {
    if (!id || validating) return;
    setValidating(true);
    try {
      const results = await validateRuleProvenance(id);
      const map = new Map<string, ProvenanceValidation>();
      for (const r of results) map.set(r.provenance_id, r);
      setProvValidation(map);
      const broken = results.filter((r) => r.status === 'broken').length;
      const ok = results.filter((r) => r.status === 'ok').length;
      const skipped = results.filter((r) => r.status === 'skipped').length;
      if (broken > 0) {
        toast.addToast(
          `${broken} broken · ${ok} ok · ${skipped} skipped`,
          'error',
        );
      } else {
        toast.addToast(
          `${ok} ok · ${skipped} skipped — no broken links`,
          'success',
        );
      }
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : String(e), 'error');
    } finally {
      setValidating(false);
    }
  }, [id, validating, toast]);

  const loadRule = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await getRule(id);
      setRule(r);
      const h = await getRuleHistory(r.slug);
      setHistory(h);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      if (cancelled) return;
      await loadRule();
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadRule, reloadToken]);

  async function onActivate() {
    if (!rule) return;
    setActionPending(true);
    try {
      await activateRule(rule.id);
      toast.addToast('Rule activated', 'success');
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(e instanceof Error ? e.message : 'Activate failed', 'error');
    } finally {
      setActionPending(false);
    }
  }

  async function onDeprecateConfirm(reason: string) {
    if (!rule) return;
    setActionPending(true);
    try {
      await deprecateRule(rule.id, reason);
      toast.addToast('Rule deprecated', 'success');
      setShowDeprecate(false);
      setReloadToken((t) => t + 1);
    } catch (e) {
      toast.addToast(
        e instanceof Error ? e.message : 'Deprecate failed',
        'error',
      );
    } finally {
      setActionPending(false);
    }
  }

  if (loading && !rule) {
    return (
      <div data-testid="admin-rule-detail" className="hub-page--reading space-y-4">
        <RoleProvisioningBanner />
        <Breadcrumbs items={[...BASE_CRUMBS, { label: 'Loading…' }]} />
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Loading…
        </div>
      </div>
    );
  }

  if (err || !rule) {
    return (
      <div data-testid="admin-rule-detail" className="hub-page--reading space-y-4">
        <RoleProvisioningBanner />
        <Breadcrumbs items={[...BASE_CRUMBS, { label: 'Not found' }]} />
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {err ?? 'Rule not found'}
        </div>
        <Button variant="ghost" onClick={() => navigate('/admin/rules')}>
          Back to rule library
        </Button>
      </div>
    );
  }

  const crumbLabel = rule.name.length > 80 ? `${rule.name.slice(0, 80)}…` : rule.name;

  return (
    <div data-testid="admin-rule-detail" className="hub-page--reading space-y-5">
      <RoleProvisioningBanner />
      <Breadcrumbs items={[...BASE_CRUMBS, { label: crumbLabel }]} />

      <header className="space-y-2 rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="hub-page__title flex items-center gap-2">
              <Icon name="bx-book-open" className="text-violet-600" />
              {rule.name}
              <SeedBadge createdBy={rule.created_by} size="md" />
            </h1>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <code className="font-mono text-xs text-slate-600">{rule.slug}</code>
              <span className="text-slate-400">·</span>
              <span className="text-slate-600">v{rule.version}</span>
              <span className="text-slate-400">·</span>
              <StatusBadge status={rule.status} />
              <EpistemicBadge status={rule.epistemic_status} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {rule.status === 'approved_inactive' && (
              <Button
                variant="primary"
                data-testid="rule-detail-activate"
                disabled={actionPending}
                onClick={onActivate}
              >
                {actionPending ? 'Working…' : 'Activate'}
              </Button>
            )}
            {rule.status === 'active' && (
              <Button
                variant="danger"
                data-testid="rule-detail-deprecate"
                disabled={actionPending}
                onClick={() => setShowDeprecate(true)}
              >
                Deprecate
              </Button>
            )}
            {rule.status === 'deprecated' && (
              <span className="rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-500">
                Archived
              </span>
            )}
          </div>
        </div>
      </header>

      <Section title="Definition">
        <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
          {rule.definition}
        </p>
      </Section>

      <Section title="Evaluation steps">
        <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 font-mono text-xs text-slate-800">
          {JSON.stringify(rule.evaluation_steps, null, 2)}
        </pre>
      </Section>

      <Section title="DAG structure">
        {rule.dag_structure ? (
          <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 font-mono text-xs text-slate-800">
            {JSON.stringify(rule.dag_structure, null, 2)}
          </pre>
        ) : (
          <p className="text-sm text-slate-500">Not applicable (g_eval rule)</p>
        )}
      </Section>

      <Section title="Applicability">
        <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
          <div>
            <dt className="text-xs font-semibold text-slate-500">
              Engines
            </dt>
            <dd className="mt-1 flex flex-wrap gap-1">
              {rule.applicability_engines.length === 0 && (
                <span className="text-slate-400">—</span>
              )}
              {rule.applicability_engines.map((e) => (
                <Chip key={e}>{e}</Chip>
              ))}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold text-slate-500">
              Content types
            </dt>
            <dd className="mt-1 flex flex-wrap gap-1">
              {rule.applicability_content_types.length === 0 && (
                <span className="text-slate-400">—</span>
              )}
              {rule.applicability_content_types.map((c) => (
                <Chip key={c}>{c}</Chip>
              ))}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold text-slate-500">
              Weight
            </dt>
            <dd className="mt-1 text-sm text-slate-700">
              {rule.weight.toFixed(2)}
            </dd>
          </div>
        </dl>
      </Section>

      <Section
        title="Provenance"
        actions={
          rule.provenance && rule.provenance.length > 0 ? (
            <Button
              variant="ghost"
              data-testid="rule-detail-validate-provenance"
              onClick={onValidateProvenance}
              disabled={validating}
            >
              <Icon name="bx-refresh" className={validating ? 'animate-spin' : ''} />
              {validating ? 'Validating…' : 'Validate URLs'}
            </Button>
          ) : null
        }
      >
        {(!rule.provenance || rule.provenance.length === 0) && (
          <p className="text-sm text-slate-500">No provenance recorded.</p>
        )}
        {rule.provenance && rule.provenance.length > 0 && (
          <ul className="space-y-3">
            {rule.provenance.map((p) => {
              const v = provValidation?.get(p.id);
              return (
                <li
                  key={p.id}
                  className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm"
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <Chip>{p.source_type}</Chip>
                    {p.source_url ? (
                      <a
                        href={p.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-violet-700 hover:underline"
                      >
                        {p.source_title ?? p.source_url}
                      </a>
                    ) : (
                      <span>{p.source_title ?? '(untitled)'}</span>
                    )}
                    {v && <ValidationPill validation={v} />}
                    <span>·</span>
                    <span>{p.source_author}</span>
                    {p.source_date && (
                      <>
                        <span>·</span>
                        <span>
                          {new Date(p.source_date).toLocaleDateString()}
                        </span>
                      </>
                    )}
                  </div>
                  {p.supporting_quote && (
                    <blockquote className="mt-2 border-l-2 border-[var(--hub-color-line-strong)] pl-3 text-sm text-slate-700">
                      {p.supporting_quote}
                    </blockquote>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="Version history">
        {history.length === 0 && (
          <p className="text-sm text-slate-500">No history available.</p>
        )}
        {history.length > 0 && (
          <ol className="space-y-2">
            {history.map((h) => (
              <li
                key={h.id}
                data-testid={`rule-history-${h.version}`}
                className={`rounded-md border p-3 text-sm ${
                  h.id === rule.id
                    ? 'border-violet-300 bg-violet-50'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-semibold text-slate-900">v{h.version}</span>
                  <StatusBadge status={h.status} />
                  <span className="text-slate-500">
                    created {new Date(h.created_at).toLocaleString()}
                  </span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-500">by {h.created_by}</span>
                  {h.id === rule.id && (
                    <KridtChip className="ml-auto">Current</KridtChip>
                  )}
                </div>
                {h.deprecation_reason && (
                  <p className="mt-1 text-xs text-rose-700">
                    Deprecated: {h.deprecation_reason}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </Section>

      {showDeprecate && (
        <DeprecateModal
          ruleName={rule.name}
          pending={actionPending}
          onCancel={() => setShowDeprecate(false)}
          onConfirm={onDeprecateConfirm}
        />
      )}
    </div>
  );
}

function Section({
  title,
  children,
  actions,
}: {
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <section className="hub-section">
      <div className="hub-section__head">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
      {children}
    </span>
  );
}

function ValidationPill({ validation }: { validation: ProvenanceValidation }) {
  if (validation.status === 'ok') {
    return (
      <span
        data-testid={`prov-validation-${validation.provenance_id}`}
        title={validation.detail ?? `HTTP ${validation.http_status}`}
        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800"
      >
        <Icon name="bx-check-circle" /> {validation.http_status ?? 'OK'}
      </span>
    );
  }
  if (validation.status === 'broken') {
    return (
      <span
        data-testid={`prov-validation-${validation.provenance_id}`}
        title={validation.detail ?? 'broken'}
        className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-800"
      >
        <Icon name="bx-x-circle" /> {validation.http_status ?? 'broken'}
      </span>
    );
  }
  // skipped
  return (
    <span
      data-testid={`prov-validation-${validation.provenance_id}`}
      title={validation.detail ?? 'skipped (non-HTTP URL)'}
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-xs font-medium text-slate-600"
    >
      <Icon name="bx-minus-circle" /> skipped
    </span>
  );
}
